import { ArrowRight, GitCompareArrows } from 'lucide-react';

import type {
  ResearchPoll,
  ResearchRoomComparisonItem,
  ResearchRoomMessage,
} from '@repo/contracts';

import { averageRating, counts, overallOrder } from './poll-summary';
import { FlotoMark } from './room-messages';

/**
 * What moved in a poll between two screens, as two short figures and the
 * direction between them — worked out here from both polls' votes, never by
 * a model.
 */
function pollDelta(
  before: ResearchPoll,
  after: ResearchPoll,
): {
  before: string;
  after: string;
  /** Whether it moved the way the product would want, when that is clear. */
  better: boolean | null;
} {
  switch (before.format) {
    case 'rating': {
      const a = averageRating(before) ?? 0;
      const b = averageRating(after) ?? 0;
      return {
        after: `${b.toFixed(1)} / 5`,
        before: `${a.toFixed(1)} / 5`,
        better: Math.abs(b - a) < 0.3 ? null : b > a,
      };
    }
    case 'yes-no': {
      const share = (poll: ResearchPoll) =>
        poll.votes.length === 0
          ? 0
          : (counts(poll)[0] ?? 0) / poll.votes.length;
      const a = share(before);
      const b = share(after);
      return {
        after: `${String(Math.round(b * 100))}% yes`,
        before: `${String(Math.round(a * 100))}% yes`,
        better: Math.abs(b - a) < 0.1 ? null : b > a,
      };
    }
    case 'ranking': {
      const top = (poll: ResearchPoll) =>
        poll.options[overallOrder(poll)[0] ?? 0] ?? '—';
      return {
        after: `1. ${top(after)}`,
        before: `1. ${top(before)}`,
        better: null,
      };
    }
    case 'choice': {
      const lead = (poll: ResearchPoll) => {
        const tally = counts(poll);
        const most = Math.max(...tally);
        return `${poll.options[tally.indexOf(most)] ?? '—'} ${String(most)}/${String(poll.votes.length)}`;
      };
      return { after: lead(after), before: lead(before), better: null };
    }
  }
}

function Item({ item }: { item: ResearchRoomComparisonItem }) {
  const delta =
    item.before && item.after ? pollDelta(item.before, item.after) : null;
  return (
    <li className="rounded-button bg-warm px-3 py-2.5">
      <p className="text-caption leading-snug font-semibold text-text-primary">
        {item.question}
      </p>
      {delta ? (
        <p className="mt-1.5 flex flex-wrap items-center gap-2 text-ui tabular-nums">
          <span className="text-text-muted">{delta.before}</span>
          <ArrowRight aria-hidden="true" className="size-3.5 text-text-muted" />
          <span
            className={[
              'font-semibold',
              delta.better === true
                ? 'text-success-ink'
                : delta.better === false
                  ? 'text-critical'
                  : 'text-text-primary',
            ].join(' ')}
          >
            {delta.after}
          </span>
        </p>
      ) : null}
      <p className="mt-1 text-caption leading-relaxed text-text-secondary">
        {item.change}
      </p>
      {!delta && (item.beforeRead || item.afterRead) ? (
        <details className="mt-1.5 text-caption text-text-secondary">
          <summary className="cursor-pointer text-tag font-semibold text-text-muted">
            What Floto heard each time
          </summary>
          <p className="mt-1">
            <span className="font-semibold text-text-primary">Before:</span>{' '}
            {item.beforeRead ?? '—'}
          </p>
          <p className="mt-1">
            <span className="font-semibold text-text-primary">After:</span>{' '}
            {item.afterRead ?? '—'}
          </p>
        </details>
      ) : null}
    </li>
  );
}

/**
 * A re-test's verdict: the same panel, the same questions, two screens — and
 * what moved. The card the whole re-test exists to produce.
 */
export function ComparisonCard({ message }: { message: ResearchRoomMessage }) {
  const comparison = message.comparison;
  return (
    <article className="rounded-card bg-surface px-4 py-3.5 ring-2 ring-ink/10 duration-300 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none">
      <header className="flex items-center gap-2">
        <FlotoMark />
        <p className="flex-1 text-ui">
          <span className="font-semibold text-text-primary">Floto</span>
          <span className="text-text-muted"> · Before and after</span>
        </p>
        <GitCompareArrows
          aria-hidden="true"
          className="size-4 text-text-muted"
        />
      </header>

      {message.status === 'pending' || !comparison ? (
        <p className="mt-2 animate-pulse text-ui text-text-muted motion-reduce:animate-none">
          {message.status === 'failed'
            ? 'Floto couldn’t compare the two screens.'
            : 'Comparing the two screens…'}
        </p>
      ) : (
        <>
          <p className="mt-2 flex flex-wrap items-center gap-1.5 text-tag font-semibold text-text-secondary">
            {/* Labelled, not just named: two versions of a page are often
                captured under the same name. */}
            <span className="max-w-40 truncate rounded-badge bg-warm px-1.5 py-0.5">
              <span className="text-text-muted">Before · </span>
              {comparison.fromScreen}
            </span>
            <ArrowRight aria-hidden="true" className="size-3" />
            <span className="max-w-40 truncate rounded-badge bg-ink px-1.5 py-0.5 text-white">
              <span className="opacity-70">After · </span>
              {comparison.toScreen}
            </span>
          </p>
          {comparison.summary ? (
            <p className="mt-2 text-ui leading-relaxed text-text-primary">
              {comparison.summary}
            </p>
          ) : null}
          <ul className="mt-3 space-y-2">
            {comparison.items.map((item) => (
              <Item item={item} key={item.question} />
            ))}
          </ul>
        </>
      )}
    </article>
  );
}
